import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, ErrorBanner, Spinner } from '@/components/ui';
import { useCardWidth } from '@/hooks/use-card-width';
import { IssueTypeLegend } from './shared';
import { TrendLineChart, type ITrendPoint } from './TrendLineChart';
import { TrendSparkline } from './TrendSparkline';
import { CARD_WIDTH_THRESHOLDS } from '@/lib/metric-view/responsive-thresholds';
import { type IBucketAxisProps } from '@/lib/metric-view/bucket-format';

interface ITrendCardProps {
  title: string;
  description: string;
  data: ITrendPoint[];
  activeType: 'all' | 'story' | 'bug' | 'task';
  loading: boolean;
  error: string | null;
  height?: number;
  afterLegend?: ReactNode;
  /** Forwarded to `TrendLineChart` — see `bucket-format.ts`. */
  axis?: IBucketAxisProps;
}

export function TrendCard({
  title,
  description,
  data,
  activeType,
  loading,
  error,
  height,
  afterLegend,
  axis,
}: Readonly<ITrendCardProps>) {
  const { ref, width } = useCardWidth<HTMLDivElement>();
  const useSparkline = width > 0 && width < CARD_WIDTH_THRESHOLDS.trend.sparkline;

  return (
    <Card ref={ref}>
      <CardHeader>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
        <p className="text-[11px] text-muted-foreground">{description}</p>
        {!useSparkline && <div className="mt-2"><IssueTypeLegend /></div>}
        {afterLegend}
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex h-40 items-center justify-center"><Spinner /></div>
        )}
        {!loading && error && <ErrorBanner message={error} />}
        {!loading && !error && data.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No data for this period.
          </p>
        )}
        {!loading && !error && data.length > 0 && (
          useSparkline
            ? <TrendSparkline data={data} activeType={activeType} />
            : <TrendLineChart data={data} activeType={activeType} height={height} axis={axis} />
        )}
      </CardContent>
    </Card>
  );
}
